import { authFetch } from '../utils/authFetch';
import { getBackendConfigInstance } from '../config/backendConfig';

export function getApiBase() {
  const baseUrl = getBackendConfigInstance().baseUrl;
  return baseUrl.endsWith('/api') ? baseUrl : `${baseUrl}/api`;
}

/** When NODE_ENV=development points at localhost:3001 but nothing is listening, retry the same path on staging Functions (same Firebase project as local dev). */
function getLocalDevFallbackApiBase() {
  const cfg = getBackendConfigInstance();
  if (cfg.type !== 'local') return null;
  const base = 'https://us-central1-artmosphere-89b64-staging.cloudfunctions.net';
  return base.endsWith('/api') ? base : `${base}/api`;
}

function rewriteUrlToFallbackIfLocal(fullUrl) {
  const fb = getLocalDevFallbackApiBase();
  if (!fb) return null;
  const primary = getApiBase();
  if (!fullUrl.startsWith(primary)) return null;
  return fb + fullUrl.slice(primary.length);
}

function isLikelyNetworkFailure(err) {
  if (!err) return false;
  if (err instanceof TypeError) return true;
  const m = typeof err.message === 'string' ? err.message : String(err);
  return /failed to fetch|load failed|networkerror|network request failed|aborted/i.test(m);
}

/** Map browser/network errors to actionable copy for onboarding UI. */
export function normalizeStudioApiError(err) {
  if (err?.name === 'AuthTokenTimeoutError' || /getIdToken timed out/i.test(err?.message || '')) {
    return 'Signing in took too long. Check your connection, refresh the page, and try again.';
  }
  if (isLikelyNetworkFailure(err)) {
    return 'Could not reach the Studio API. If you use local dev, start the backend on port 3001 (or the app will try the staging API once when localhost is unreachable).';
  }
  return err?.message || String(err) || 'Something went wrong.';
}

function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

async function authFetchWithTimeout(url, options = {}, timeoutMs = 20000) {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
  try {
    return await authFetch(url, { ...options, signal: controller.signal });
  } finally {
    clearTimeout(timeoutId);
  }
}

/** Retries transient failures so Artmo validate/redeem can complete under load or brief outages. */
async function authFetchWithRetry(url, options = {}, timeoutMs = 20000, maxAttempts = 4) {
  let lastErr;
  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    try {
      const res = await authFetchWithTimeout(url, options, timeoutMs);
      const retryableStatus =
        res.status === 429 || res.status === 502 || res.status === 503 || res.status === 504;
      if (retryableStatus && attempt < maxAttempts - 1) {
        await sleep(500 * 2 ** attempt);
        continue;
      }
      if (res.status >= 500 && res.status < 600 && attempt < maxAttempts - 1) {
        await sleep(400 * 2 ** attempt);
        continue;
      }
      return res;
    } catch (e) {
      lastErr = e;
      const aborted = e?.name === 'AbortError';
      const network =
        e instanceof TypeError ||
        (typeof e?.message === 'string' &&
          /failed to fetch|network|load failed|aborted/i.test(e.message));
      if (attempt < maxAttempts - 1 && (aborted || network)) {
        await sleep(450 * 2 ** attempt);
        continue;
      }
      throw e;
    }
  }
  throw lastErr;
}

/** When localhost responds but the wrong server/proxy returns 403/404, retry the same path on staging Functions. */
const LOCAL_HTTP_FALLBACK_STATUSES = new Set([401, 403, 404, 502, 503, 504]);

async function authFetchWithRetryAndLocalFallback(url, options = {}, timeoutMs = 20000, maxAttempts = 4) {
  const alt = rewriteUrlToFallbackIfLocal(url);
  if (!alt) {
    return await authFetchWithRetry(url, options, timeoutMs, maxAttempts);
  }
  try {
    const res = await authFetchWithRetry(url, options, timeoutMs, maxAttempts);
    if (!res.ok && LOCAL_HTTP_FALLBACK_STATUSES.has(res.status)) {
      try {
        return await authFetchWithRetry(alt, options, timeoutMs, maxAttempts);
      } catch {
        return res;
      }
    }
    return res;
  } catch (e) {
    if (!isLikelyNetworkFailure(e)) throw e;
    return await authFetchWithRetry(alt, options, timeoutMs, maxAttempts);
  }
}

async function publicFetchJsonWithLocalFallback(path) {
  const primary = `${getApiBase()}${path}`;
  const tryAltOnHttp = async (res) => {
    const alt = rewriteUrlToFallbackIfLocal(primary);
    if (!alt || res.ok) return null;
    if (![401, 403, 404, 502, 503, 504].includes(res.status)) return null;
    const res2 = await fetch(alt);
    return res2.ok ? res2.json() : null;
  };
  try {
    const res = await fetch(primary);
    if (!res.ok) {
      const fromAlt = await tryAltOnHttp(res);
      if (fromAlt) return fromAlt;
      throw new Error('Failed to load membership config');
    }
    return res.json();
  } catch (e) {
    const alt = rewriteUrlToFallbackIfLocal(primary);
    if (!alt || !isLikelyNetworkFailure(e)) throw e;
    const res = await fetch(alt);
    if (!res.ok) throw new Error('Failed to load membership config');
    return res.json();
  }
}

/** Public: premium add-on price from Firestore (admin-configurable). */
export async function fetchPublicMembershipConfig() {
  return publicFetchJsonWithLocalFallback('/membership-public/membership-config');
}

const VALIDATE_TIMEOUT_MS = 45000;
/** Redeem should fail fast enough that the UI never feels stuck (retries × timeout add up). */
const REDEEM_TIMEOUT_MS = 28000;
const REDEEM_MAX_ATTEMPTS = 2;

export async function validateArtmoCodes(codes) {
  try {
    const res = await authFetchWithRetryAndLocalFallback(
      `${getApiBase()}/membership/artmo/validate-codes`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ codes }),
      },
      VALIDATE_TIMEOUT_MS
    );
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.error || 'Validation failed');
    return data;
  } catch (e) {
    if (e?.name === 'AbortError') {
      throw new Error('Validation timed out after retries. Please try again.');
    }
    throw e;
  }
}

export async function redeemArtmoCodes(codes) {
  try {
    const res = await authFetchWithRetryAndLocalFallback(
      `${getApiBase()}/membership/artmo/redeem-codes`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ codes }),
      },
      REDEEM_TIMEOUT_MS,
      REDEEM_MAX_ATTEMPTS
    );
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      const parts = [data.error, data.details].filter((x) => typeof x === 'string' && x.trim());
      const deduped = parts.filter((p, i) => parts.indexOf(p) === i);
      throw new Error(deduped.join(' — ') || 'Redeem failed');
    }
    return data;
  } catch (e) {
    if (e?.name === 'AbortError') {
      throw new Error('Code redemption timed out after retries. Please try again.');
    }
    if (e?.name === 'AuthTokenTimeoutError' || /getIdToken timed out/i.test(e?.message || '')) {
      throw new Error(
        'Signing in took too long. Refresh the page and try again, or check that you are online.'
      );
    }
    throw e;
  }
}

/**
 * Primary backend first. When config is `local` (localhost:3001) and the browser gets a network
 * error (backend not running), try staging Functions once — same project as local dev auth.
 * No multi-retry loops; server still logs + persists subscription end.
 */
async function completeOnboardingRequest(url, options, timeoutMs) {
  try {
    return await authFetchWithTimeout(url, options, timeoutMs);
  } catch (e) {
    if (!isLikelyNetworkFailure(e)) throw e;
    const alt = rewriteUrlToFallbackIfLocal(url);
    if (!alt) throw e;
    return await authFetchWithTimeout(alt, options, timeoutMs);
  }
}

export async function completeOnboarding(action) {
  const url = `${getApiBase()}/membership/onboarding/complete`;
  const res = await completeOnboardingRequest(
    url,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action }),
    },
    25000
  );
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || 'Request failed');
  return data;
}

/**
 * Premium add-on: server prorates to your current period end (admin rate × remaining 30-day months).
 * Pass `{ dryRun: true }` for a quote only (no Razorpay order).
 */
export async function createPremiumAddonRazorpayOrder(body = {}) {
  const payload = typeof body === 'number' ? {} : body;
  const res = await authFetchWithRetryAndLocalFallback(`${getApiBase()}/membership/razorpay/create-premium-addon-order`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  }, 30000, 3);
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const base =
      data.error === 'razorpay_not_configured'
        ? 'Payment gateway is not configured yet. Contact support.'
        : data.error || data.details || 'Could not start payment';
    const hint =
      res.status === 403
        ? ' (403 — often CORS or blocked request; use http://localhost:3000 or add your origin to backend CORS.)'
        : '';
    throw new Error(`${base}${hint}`);
  }
  return data;
}

/** Server-side quote for the prorated add-on (no payment). */
export async function fetchPremiumAddonQuote() {
  return createPremiumAddonRazorpayOrder({ dryRun: true });
}

/** After Razorpay Checkout success, verify signature server-side and apply premium. */
export async function verifyPremiumAddonRazorpay(payload) {
  const res = await authFetchWithRetryAndLocalFallback(`${getApiBase()}/membership/razorpay/verify-premium-addon`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  }, 30000, 3);
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.message || data.error || data.details || 'Payment verification failed');
  return data;
}

/** Dev-only: simulated checkout when backend has ALLOW_PREMIUM_CHECKOUT_WITHOUT_PAYMENT=true */
export async function checkoutPremiumAddon() {
  const res = await authFetchWithRetryAndLocalFallback(`${getApiBase()}/membership/checkout`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ plan: 'premium-addon' }),
  }, 30000, 3);
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = new Error(data.message || data.error || 'Checkout failed');
    err.code = data.error;
    err.status = res.status;
    throw err;
  }
  return data;
}
